/* eslint-disable */

import {
  endDelegatedSession,
  getControlSessionEvents,
  getDelegatedSessionsById,
  getSessionEvents,
} from '@/api/oversight';
import { DelegatedSessions } from '@/api/oversight/types';
import Snackbar from '@/components/Snackbar';
import { IconArrowForwardMS, IconCloseMS } from '@/constants/icons';
import { GlobalContext } from '@/context/global';
import {
  formatDateLongMonth,
  formatMillisToMinutesSeconds,
} from '@/helpers';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { Button } from '@/pages/Oversight/components/Button';
import { Timeline, TimelineEvent } from '@/pages/Oversight/components/Timeline';
import { WarningOutlined } from '@mui/icons-material';
import { Divider, IconButton, Tab, Tooltip } from '@mui/material';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { StatusCode } from '@/api/enumerations';
import { EndSessionConfirmDialog } from '@/pages/Oversight/components/EndSessionConfirmDialog';
import { SubtitleCell } from '@/pages/Oversight/components/Table/styles';
import { RoundedGreen } from '@/pages/Oversight/styles';
import {
  delegatedAccessLabels,
  expiredDetail,
  formatDate,
} from '@/pages/Oversight/utils';
import '@sinm/react-chrome-tabs/css/chrome-tabs.css';
import { StyledTitle } from '../CustomerDetails/styles';
import {
  ActiveStatus,
  Container,
  ContainerInfo,
  DateTypography,
  DurationTypography,
  Label,
  Span,
  TopContainer,
} from './styles';

type SectionClientProps = {
  id?: string;
  sessionId?: number;
  source?: 'delegated_access' | 'section_client' | 'section_adm_user';
  userRole?: string;
  error_count?: number;
  device?: string;
  started_at?: string | null;
  ended_at?: string | null;
  delegated_session_id?: number | null;
  showCloseButton?: boolean;
  onClose: () => void;
  onActivityClick?: (delegatedSessionId: number) => void;
  onSessionEnded?: () => void;
};

type Tab = {
  id: string;
  title: string;
  active?: boolean;
};

function getSessionDuration(
  startedAt?: string | null,
  endedAt?: string | null
): number | null {
  if (!startedAt || !endedAt) return null;

  const durationMs =
    new Date(endedAt).getTime() - new Date(startedAt).getTime();

  return Number.isNaN(durationMs) ? null : durationMs;
}

function getStatusDate(session: DelegatedSessions): string | null {
  if (session.status === 'rejected') return session.consent_responded_at;
  if (session.status === 'ended') {
    return session.session_ended_at ?? session.last_activity_at;
  }

  return session.last_activity_at;
}

function formatStatusDate(session: DelegatedSessions): string {
  const date = getStatusDate(session);

  return date ? formatDate(date, 'absolute') : '-';
}

export function SectionClient({
  id,
  sessionId,
  source,
  onClose,
  onActivityClick,
  onSessionEnded,
  userRole,
  error_count,
  device,
  started_at = null,
  ended_at = null,
  delegated_session_id = null,
  showCloseButton = true,
}: SectionClientProps): JSX.Element {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [records, setRecords] = useState(0);
  const [, os, browser] = device?.split(' / ') || ['', '', ''];
  const [browserName, browserVersion] = (browser ?? '').split(/ (.+)/);
  const [delegatedSessions, setDelegatedSessions] =
    useState<DelegatedSessions>();
  const [openEndSessionDialog, setOpenEndSessionDialog] = useState(false);
  const [endingSession, setEndingSession] = useState(false);

  const sessionDurationMs =
    source === 'delegated_access'
      ? getSessionDuration(
          delegatedSessions?.session_started_at,
          delegatedSessions?.session_ended_at
        )
      : getSessionDuration(started_at, ended_at);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const getDelegatedSessionData = useCallback(
    async (delegatedSessionId: number) => {
      try {
        const { data, detail } = await getDelegatedSessionsById(
          delegatedSessionId
        );

        if (detail?.description) {
          throw new Error(detail.description);
        }

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        if (!data) throw new Error('Algo deu errado, tente novamente.');

        setDelegatedSessions(data);
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [handleSnackbar, getErrorMessage]
  );

  const isControlSession = source === 'section_adm_user';

  const hasEventsSource = Boolean(sessionId || delegated_session_id);

  const fetchEvents = async (pageToLoad: number): Promise<void> => {
    if (!hasEventsSource) return;

    try {
      const { data, detail } = isControlSession
        ? await getControlSessionEvents(sessionId, pageToLoad)
        : await getSessionEvents(
            sessionId,
            delegated_session_id ?? undefined,
            pageToLoad
          );

      if (detail?.description) throw new Error(detail.description);

      if (detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente');
      }

      if (!data) return;

      const pageEvents: TimelineEvent[] =
        'control_activity_events' in data
          ? data.control_activity_events
          : data.session_events;

      setEvents((prev) =>
        pageToLoad === 1 ? pageEvents : [...prev, ...pageEvents]
      );

      setTotalPages(data.pages);
      setPage(data.current_page);
      setRecords(data.records);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const titleMap = {
    delegated_access: 'Acesso Delegado',
    section_client: 'Sessão do Cliente',
    section_adm_user: 'Sessão do Usuário Administrativo',
  };

  const renderDelegatedStatus = (row: DelegatedSessions): ReactNode => {
    if (delegatedSessions) {
      const label =
        delegatedAccessLabels[delegatedSessions?.status] ?? row?.status;
      const detail = expiredDetail(row);

      if (!detail) {
        return label;
      }

      return (
        <>
          {label}
          <Tooltip title={detail.tooltip} placement="top">
            <SubtitleCell>{detail.label}</SubtitleCell>
          </Tooltip>
        </>
      );
    }
  };

  const { openSnackbar } = useContext(GlobalContext);

  const endDelegatedAccess = useCallback(
    async (id: number) => {
      setEndingSession(true);
      try {
        const { data, detail } = await endDelegatedSession(Number(id));

        if (detail?.description) {
          throw new Error(detail.description);
        }

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        if (!data) throw new Error('Algo deu errado, tente novamente.');

        setDelegatedSessions(data);
        setOpenEndSessionDialog(false);
        onSessionEnded?.();
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        setEndingSession(false);
      }
    },
    [handleSnackbar, getErrorMessage, onSessionEnded]
  );

  useEffect(() => {
    setEvents([]);
    setPage(1);
    setTotalPages(1);
    setRecords(0);
    setDelegatedSessions(undefined);

    if (delegated_session_id) {
      getDelegatedSessionData(delegated_session_id);
    }

    fetchEvents(1);
  }, [sessionId, delegated_session_id]);

  return (
    <Container>
      {showCloseButton && (
        <TopContainer>
          <IconButton onClick={() => onClose()}>{IconCloseMS}</IconButton>
        </TopContainer>
      )}
      <StyledTitle>
        {source ? titleMap[source] : 'Sessão do cliente'}
      </StyledTitle>

      <ContainerInfo>
        <Span>
          <Label>Status:</Label>{' '}
          {delegatedSessions &&
          delegatedSessions.status === 'active' &&
          userRole === 'supervisor' ? (
            <>
              <ActiveStatus>
                <RoundedGreen />
                Ativa
              </ActiveStatus>
              <Button
                variant="outlined"
                rounded
                isRedButton
                onClick={() => setOpenEndSessionDialog(true)}
              >
                Encerrar sessão
              </Button>
            </>
          ) : delegatedSessions?.status ? (
            <>
              {renderDelegatedStatus(delegatedSessions)} {IconArrowForwardMS}{' '}
              {formatStatusDate(delegatedSessions)}
            </>
          ) : ended_at ? (
            <>
              Sessão encerrada {IconArrowForwardMS}{' '}
              {ended_at && formatDate(ended_at, 'absolute')}
            </>
          ) : (
            <ActiveStatus>
              <RoundedGreen /> Ativa
            </ActiveStatus>
          )}
        </Span>

        <Span>
          <Label>Atividade:</Label> {records || 0}
        </Span>
        <Span>
          <Label>Erros:</Label> <WarningOutlined fontSize="small" />{' '}
          {error_count || 0}
        </Span>
        <Span>
          <Label>Versão:</Label> {browserVersion || 'Desconhecida'}
        </Span>
        <Span>
          <Label>Sistema operacional:</Label> {os || 'Desconhecido'}
        </Span>
        <Span>
          <Label>Navegador:</Label> {browserName || 'Desconhecido'}
        </Span>
      </ContainerInfo>

      <Divider sx={{ margin: '32px 0' }} />

      <DateTypography>
        {events[0] && formatDateLongMonth(events[0].occurred_at)}
      </DateTypography>
      <DurationTypography>
        {sessionDurationMs !== null &&
          `Duração: ${formatMillisToMinutesSeconds(sessionDurationMs)}`}
      </DurationTypography>

      <Timeline
        source={source}
        events={hasEventsSource ? events : undefined}
        currentDelegatedSessionId={delegated_session_id}
        onActivityClick={onActivityClick}
      />
      {hasEventsSource && page < totalPages && (
        <Button variant="text" onClick={() => fetchEvents(page + 1)}>
          Carregar mais
        </Button>
      )}
      {openSnackbar && <Snackbar />}
      <EndSessionConfirmDialog
        open={openEndSessionDialog}
        onClose={() => setOpenEndSessionDialog(false)}
        onConfirm={() => endDelegatedAccess(Number(delegated_session_id))}
        loading={endingSession}
      />
    </Container>
  );
}
